import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, BedDouble, IndianRupee, Building2, FileText, Heart } from "lucide-react";
import LeadCaptureModal from "./LeadCaptureModal";
import { LEAD_CHANNELS } from "../../constants/data";

const GOLD = "#50C878";

const STATUS_COLORS = {
  "Ready to Move": { bg: "rgba(34,197,94,0.15)", color: "#4ade80" },
  "Under Construction": { bg: "rgba(251,191,36,0.15)", color: "#7FD9A0" },
  Available: { bg: "rgba(96,165,250,0.15)", color: "#1B4A7A" },
};

export default function PropertyCard({ property }) {
  const [showModal, setShowModal] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const navigate = useNavigate();

  const statusStyle = STATUS_COLORS[property.status] || { bg: "rgba(80,200,120,0.10)", color: "rgba(1,50,32,0.55)" };

  return (
    <>
      <div
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
          border: "1px solid rgba(80,200,120,0.2)",
          borderRadius: "12px",
          overflow: "hidden",
          cursor: "pointer",
          transition: "transform 0.25s, box-shadow 0.25s, border-color 0.25s",
          position: "relative",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(80,200,120,0.4)";
          e.currentTarget.style.borderColor = "rgba(80,200,120,0.5)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.borderColor = "rgba(80,200,120,0.2)";
        }}
        onClick={() => navigate(`/property/${property.id}`)}
      >
        {/* Image */}
        <div style={{ position: "relative", height: "240px", overflow: "hidden" }}>
          {!imgLoaded && (
            <div style={{ position: "absolute", inset: 0, background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: "40px", height: "40px", border: "2px solid rgba(80,200,120,0.3)", borderTopColor: GOLD, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            </div>
          )}
          <img
            src={property.images[0]}
            alt={property.title}
            onLoad={() => setImgLoaded(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.4s ease",
              opacity: imgLoaded ? 1 : 0,
            }}
            onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
            onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
          />

          {/* Type badge */}
          {/* <div
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(80,200,120,0.4)",
              borderRadius: "4px",
              padding: "4px 10px",
              color: GOLD,
              fontSize: "0.72rem",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {property.type}
          </div> */}

          {/* Status */}
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: statusStyle.bg,
              borderRadius: "4px",
              padding: "4px 10px",
              color: statusStyle.color,
              fontSize: "0.72rem",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
            }}
          >
            {property.status}
          </div>

          {/* Brochure indicator */}
          {property.brochure && (
            <div
              style={{
                position: "absolute",
                bottom: "12px",
                right: "12px",
                background: GOLD,
                borderRadius: "4px",
                padding: "4px 10px",
                color: "#FFFFFF",
                fontSize: "0.7rem",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(property.brochure, "_blank");
              }}
            >
              <FileText size={11} /> Brochure
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: "20px" }}>
          <div style={{ marginBottom: "6px" }}>
            <h3
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.35rem",
                fontWeight: 600,
                color: "#013220",
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {property.title}
            </h3>
          </div>

          <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.78rem", marginBottom: "14px", fontFamily: "'Inter', sans-serif" }}>
            by {property.builder}
          </p>

          <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "rgba(1,50,32,0.85)", fontSize: "0.82rem", fontFamily: "'Inter', sans-serif" }}>
              <MapPin size={13} color={GOLD} /> {property.area}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "rgba(1,50,32,0.85)", fontSize: "0.82rem", fontFamily: "'Inter', sans-serif" }}>
              <BedDouble size={13} color={GOLD} /> {property.bhk}
            </span>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "10px" }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowModal(true)}
              style={{
                flex: 1,
                padding: "10px",
                background: "linear-gradient(135deg, #50C878, #7FD9A0)",
                border: "none",
                borderRadius: "6px",
                color: "#FFFFFF",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: "0.82rem",
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
            >
              I'm Interested
            </button>
            <button
              onClick={() => navigate(`/property/${property.id}`)}
              style={{
                flex: 1,
                padding: "10px",
                background: "transparent",
                border: "1px solid rgba(80,200,120,0.4)",
                borderRadius: "6px",
                color: GOLD,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.82rem",
                cursor: "pointer",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "rgba(80,200,120,0.1)")}
              onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
            >
              View Details
            </button>
          </div>
        </div>
      </div>

      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        propertyTitle={property.title}
        propertyId={property.id}
        channel={LEAD_CHANNELS.WEB}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
